/**
 * lobby.ts — the PURE race-lobby contract shared by the Cloudflare referee
 * (workers/room.ts) and the game client (Flow / RaceLobbyScreen). M63: online
 * rooms front a real LOBBY now — every racer picks a ride in it and locks in
 * READY; the race begins only when someone has pressed START (friend rooms;
 * matchmade rooms auto-request) AND everyone in the lobby is ready.
 *
 * Framework-free so the start gate is unit-testable off the network, exactly
 * like matchmaking.ts.
 */

/** One grid seat as the ROSTER broadcast describes it. A "filled" seat is a
 *  racer in the lobby — a connected human, or (matchmade rooms) a staged
 *  "player" join: the AI backfill, presented identically and never revealed
 *  (owner-locked, M44). */
export interface LobbySeat {
  slot: number;
  filled: boolean;
  ready: boolean;
  /** The seat's current ride ('' while the seat is empty). Live — it changes
   *  as the racer cycles vehicles, so every client sees the pick happen. */
  vehicle: string;
  /** M81: the seat's equipped cosmetics as a serialized loadout string
   *  (config/cosmetics serializeLoadout; '' = stock). Optional so a client
   *  talking to an older worker degrades to stock looks. */
  look?: string;
  /** M110: the seat's REAL display name — the Racer ID username (or a guest's
   *  local nickname) carried on the join URL. '' = none: the client falls back
   *  to the deterministic pool handle (onlineRacerName), which is also what
   *  every AI-backfilled seat wears. Optional for older-server compat. */
  name?: string;
}

/** M110 — sanitize a display name off the wire (join URLs are user-controlled).
 *  Mirrors SaveData.sanitizeNickname (strip control chars, trim, cap 12) but
 *  lives here so BOTH room shells and the client share one rule without the
 *  server pulling in the whole save module. */
export function sanitizeNetName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return [...raw]
    .filter((ch) => {
      const cp = ch.codePointAt(0) ?? 0;
      return cp >= 0x20 && cp !== 0x7f;
    })
    .join('')
    .trim()
    .slice(0, 12);
}

/** M140: a PUBLIC crew room's search state. 'off' = the crew is gathering
 *  (friend-style lobby, no timers); 'on' = FIND RACERS was pressed — the empty
 *  seats are open to Quick Match searchers for CREW_SEARCH_MS; 'done' = the
 *  rest staged in as AI "players" and the grid auto-starts (matchmade rules). */
export type CrewSearchState = 'off' | 'on' | 'done';

/** Server → clients on every lobby change. */
export interface RosterMsg {
  t: 'roster';
  seats: LobbySeat[];
  /** START has been pressed (or the room auto-starts) — the race begins the
   *  moment every filled seat is ready. */
  startRequested: boolean;
  /** M140, public crew rooms only (additive — older clients ignore it). */
  search?: CrewSearchState;
  /** M140: ms left in the search window when this roster was sent. */
  searchMs?: number;
}

/** M140: how long a public crew room holds its empty seats open for Quick
 *  Match searchers after FIND RACERS. The matchmaker anchor the requesting
 *  client opens is sized from the room's own countdown (searchMs on the
 *  roster) minus CREW_ANCHOR_MARGIN_MS, so a stranger the matchmaker routes
 *  in always lands before the seats close (M140b — it used to be a fixed
 *  10 s started late, which could outlive the room's window). */
export const CREW_SEARCH_MS = 12_000;
export const CREW_ANCHOR_MARGIN_MS = 1_500;

/** Server → clients: the grid is set — the race builds + counts down next.
 *  The client plays the race-intro animation over this window (city splash or
 *  the World Tour Cup globe), timed so the 3-2-1 countdown is still in full
 *  view when the intro fades. */
export interface BeginMsg {
  t: 'begin';
  city: string;
  tourLen: number;
  raceNum: number;
}

/** How long the server holds the finished grid before starting the race sim —
 *  the intro-animation window. The sim's own 3 s countdown plays AFTER this,
 *  so the intro never eats the 3-2-1. */
export const LOBBY_STAGING_MS = 3500;

/** How many racers must be in a friend lobby before START RACE is offered
 *  (the solo host keeps a separate "race the AI now" escape hatch). */
export const LOBBY_MIN_TO_START = 2;

/**
 * The one start gate: a requested start fires only when EVERY racer in the
 * lobby is ready — connected humans and (matchmade) staged joins alike, so a
 * matchmade grid never starts "before" one of its presented players is set.
 * No humans connected = nothing to start.
 */
export function canBeginRace(opts: {
  startRequested: boolean;
  /** Ready flags of the CONNECTED humans. */
  humansReady: boolean[];
  /** Matchmade staged seats' ready flags (empty for friend rooms). */
  stagedReady?: boolean[];
}): boolean {
  const staged = opts.stagedReady ?? [];
  if (!opts.startRequested || opts.humansReady.length === 0) return false;
  return opts.humansReady.every(Boolean) && staged.every(Boolean);
}

/**
 * Parse whatever the player typed/pasted into a friend room code: a bare
 * "ABCD" (any case, stray spaces), or a whole invite link (?room=ABCD).
 * Returns the canonical uppercase code, or null if it can't be one.
 */
export function parseJoinCode(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  const fromLink = /[?&]room=([A-Za-z0-9-]+)/.exec(s);
  const raw = (fromLink ? fromLink[1] : s).trim().toUpperCase();
  // Friend codes are 4 chars from a lookalike-free alphabet (Flow.makeRoomCode)
  // + since V2 Phase B a 5th REGION letter; accept up to 8 to stay
  // forward-compatible. mm- codes are internal-only.
  return /^[A-Z0-9]{4,8}$/.test(raw) ? raw : null;
}

/** V2 Phase B (M107): the 5th character of a friend code names the room's HOST
 *  region, so a typed code lands the friend on the server the room actually
 *  lives on (invite links carry `&h=` for the same job; bare 4-char legacy
 *  codes fall back to the default host — consistent on every client). The
 *  letter is part of the room id itself — servers never parse it. */
export const ROOM_CODE_HOST_SUFFIX: Record<string, string> = {
  us: 'U',
  eu: 'E',
  asia: 'A',
  cf: 'C',
};

/** The host key a (parsed, uppercase) friend code pins, or null for legacy /
 *  unknown codes (join the default host). */
export function hostKeyForRoomCode(code: string): string | null {
  if (code.length !== 5) return null;
  const suffix = code[4];
  for (const [key, s] of Object.entries(ROOM_CODE_HOST_SUFFIX)) {
    if (s === suffix) return key;
  }
  return null;
}
